import React, { useState, useRef, useEffect, useId } from 'react'
import { Link } from 'react-router-dom'

/**
 * Kubisk Bézier easing funktion for cubic-bezier(0.22, 1, 0.36, 1)
 * Bruges til variation 03 – Markant: 480ms ultra-blød og dynamisk formændring.
 */
function cubicBezier(x1, y1, x2, y2) {
  return function (t) {
    if (t <= 0) return 0
    if (t >= 1) return 1
    let u = t
    // 6 trin Newton-Raphson for præcis x(u) inversion
    for (let i = 0; i < 6; i++) {
      const cx = 3 * (1 - u) * (1 - u) * u * x1 + 3 * (1 - u) * u * u * x2 + u * u * u
      const dx = cx - t
      if (Math.abs(dx) < 1e-4) break
      const slope = 3 * (1 - u) * (1 - u) * x1 + 6 * (1 - u) * u * (x2 - x1) + 3 * u * u * (1 - x2)
      if (Math.abs(slope) < 1e-4) break
      u -= dx / slope
    }
    return 3 * (1 - u) * (1 - u) * u * y1 + 3 * (1 - u) * u * u * y2 + u * u * u
  }
}

const easeOutArc = cubicBezier(0.22, 1, 0.36, 1)

/**
 * Genererer SVG path d-streng for Arc & Orbit knapformen.
 * Interpolerer mellem normal tilstand (bred, blød asymmetrisk bue)
 * og hover tilstand (variation 03 – Markant: kraftigt tilspidset højre ende).
 *
 * @param {number} w Knappens bredde i pixels
 * @param {number} h Knappens højde i pixels
 * @param {number} progress Værdi mellem 0.0 (normal) og 1.0 (hover/markant spids)
 */
function computeArcOrbitPath(w, h, progress) {
  const p = Math.max(0, Math.min(1, progress))

  // Venstre side er stabil og blød
  const leftRadius = Math.min(26, h * 0.45)
  const topStart = leftRadius
  const bottomEnd = leftRadius

  // Højre ende geometri:
  // Normal (p=0):
  //   Øvre kurve starter ved w - 46, går mod (w, h * 0.50) via fyldige kontrolpunkter (w - 18, 0) og (w, h * 0.22).
  //   Nedre kurve starter ved (w, h * 0.50) og runder fyldigt til (w - 40, h).
  // Hover / Markant (p=1):
  //   Skuldrene trækker sig markant indad for at skabe en tydelig spids ved (w, h * 0.50).
  //   Øvre kurve trækker straks ind til (w - 32, 1) og strømliner ind mod spidsen (w - 8, h * 0.35).
  //   Nedre kurve strømliner væk fra spidsen (w - 8, h * 0.65) og trækker ind til (w - 28, h - 1).
  const topAnchorX = w - 46
  const bottomAnchorX = w - 40

  // Kontrolpunkter for øvre højre bue
  const c1x = (w - 18) + ((w - 32) - (w - 18)) * p
  const c1y = 0 + (1 - 0) * p

  const c2x = w + ((w - 8) - w) * p
  const c2y = (h * 0.22) + ((h * 0.35) - (h * 0.22)) * p

  // Spidsen ved højre yderkant (bredden w forbliver stabil)
  const tipX = w
  const tipY = h * 0.50

  // Kontrolpunkter for nedre højre bue
  const c3x = w + ((w - 8) - w) * p
  const c3y = (h * 0.78) + ((h * 0.65) - (h * 0.78)) * p

  const c4x = (w - 18) + ((w - 28) - (w - 18)) * p
  const c4y = h + ((h - 1) - h) * p

  return [
    `M ${topStart} 0`,
    // Topkant (overvejende vandret)
    `C ${w * 0.35} 0, ${w * 0.70} 0, ${topAnchorX} 0`,
    // Højre øvre overgang mod spids
    `C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${tipX.toFixed(2)} ${tipY.toFixed(2)}`,
    // Højre nedre overgang fra spids
    `C ${c3x.toFixed(2)} ${c3y.toFixed(2)}, ${c4x.toFixed(2)} ${c4y.toFixed(2)}, ${bottomAnchorX} ${h}`,
    // Bundkant
    `C ${w * 0.70} ${h}, ${w * 0.35} ${h}, ${bottomEnd} ${h}`,
    // Venstre bund bue
    `C ${leftRadius * 0.4} ${h}, 0 ${h * 0.78}, 0 ${h * 0.50}`,
    // Venstre top bue
    `C 0 ${h * 0.22}, ${leftRadius * 0.4} 0, ${topStart} 0`,
    'Z',
  ].join(' ')
}

/**
 * Den vandrette Rising Flow pil
 * Hvid, elegant let kurvet streg, enkel åben pilespids, uden cirkel eller separat baggrund.
 */
export function RisingFlowArrow({ className = '', style = {} }) {
  return (
    <svg
      viewBox="0 0 40 18"
      className={`shrink-0 pointer-events-none ${className}`}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Elegant, let kurvet vandret streg med blød fremdrift */}
      <path d="M 2 9 C 11 8.2, 22 9.8, 35 9" />
      {/* Enkel, åben pilespids */}
      <path d="M 26 3.8 L 36 9 L 26 14.2" />
    </svg>
  )
}

/**
 * ArcOrbitButton
 * Implementering af "Arc & Orbit med Shape Morphing (variation 03 – Markant)"
 *
 * Egenskaber:
 * - Baggrundsfarve: #007A53 (Pantone 341)
 * - Hover: #005F40 (farveovergang synkront med form)
 * - Tekst: Hvid, Montserrat 700, 14–16 px
 * - Pil: Hvid, vandret Rising Flow, 3–5 px diskret glide ved hover
 * - Asymmetrisk form: blød venstre side, stor afrundet højre ende -> markant spids ved hover
 * - Animationsspecifikation: 480 ms, cubic-bezier(0.22, 1, 0.36, 1)
 * - Ingen layout shift, ingen rotation, ingen scale, ingen translateY
 * - Respekterer prefers-reduced-motion
 */
export function ArcOrbitButton({
  children,
  to,
  href,
  onClick,
  type = 'button',
  variant = 'primary', // 'primary' | 'secondary' | 'white' | 'outline' | 'outline-white'
  arrow = true,
  icon = null,
  className = '',
  disabled = false,
  ariaLabel,
  ...props
}) {
  const containerRef = useRef(null)
  const animFrameRef = useRef(null)
  const [dimensions, setDimensions] = useState({ w: 220, h: 62 })
  const [progress, setProgress] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const filterId = useId()

  // Mål knappens reelle bredde og højde uden at forstyrre layout
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const updateDimensions = () => {
      const rect = el.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        setDimensions({
          w: Math.round(rect.width),
          h: Math.round(rect.height),
        })
      }
    }

    updateDimensions()

    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        updateDimensions()
      })
      ro.observe(el)
    }

    window.addEventListener('resize', updateDimensions)
    return () => {
      if (ro) ro.disconnect()
      window.removeEventListener('resize', updateDimensions)
    }
  }, [])

  // 480 ms cubic-bezier(0.22, 1, 0.36, 1) path-morphing loop
  useEffect(() => {
    // Tjek om brugeren foretrækker reduceret bevægelse
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReducedMotion) {
      setProgress(0)
      return
    }

    const targetProgress = isHovered || isFocused ? 1 : 0
    const startProgress = progress
    const duration = 480 // 480 ms iht. specifikationen
    const startTime = performance.now()

    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current)
    }

    const step = (now) => {
      const elapsed = now - startTime
      const rawT = Math.min(1, elapsed / duration)
      const easedT = easeOutArc(rawT)
      const current = startProgress + (targetProgress - startProgress) * easedT

      setProgress(current)

      if (rawT < 1) {
        animFrameRef.current = requestAnimationFrame(step)
      }
    }

    animFrameRef.current = requestAnimationFrame(step)

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current)
      }
    }
  }, [isHovered, isFocused])

  // Farvestyring per variant iht. specifikation og eksisterende identitet
  const activeHover = isHovered || isFocused
  let fillColor = '#007A53' // Primær Pantone 341
  let strokeColor = 'none'
  let strokeWidth = 0
  let textColorClass = 'text-white'
  let arrowColorClass = 'text-white'

  if (variant === 'secondary') {
    fillColor = activeHover ? '#24585B' : '#2F6F73'
    textColorClass = 'text-white'
    arrowColorClass = 'text-white'
  } else if (variant === 'white') {
    fillColor = activeHover ? '#F4F8F6' : '#FFFFFF'
    textColorClass = activeHover ? 'text-[#005F40]' : 'text-[#007A53]'
    arrowColorClass = activeHover ? 'text-[#005F40]' : 'text-[#007A53]'
  } else if (variant === 'outline') {
    fillColor = activeHover ? '#DCEEF2' : 'transparent'
    strokeColor = activeHover ? '#005F40' : '#007A53'
    strokeWidth = 2
    textColorClass = activeHover ? 'text-[#005F40]' : 'text-[#007A53]'
    arrowColorClass = activeHover ? 'text-[#005F40]' : 'text-[#007A53]'
  } else if (variant === 'outline-white') {
    fillColor = activeHover ? 'rgba(255, 255, 255, 0.15)' : 'transparent'
    strokeColor = '#FFFFFF'
    strokeWidth = 2
    textColorClass = activeHover ? 'text-[#DCEEF2]' : 'text-white'
    arrowColorClass = activeHover ? 'text-[#DCEEF2]' : 'text-white'
  } else {
    // Primær Pantone 341 (standard)
    fillColor = activeHover ? '#005F40' : '#007A53'
  }

  const pathD = computeArcOrbitPath(dimensions.w, dimensions.h, progress)

  // Fælles wrapper styling: ingen layout shift, ingen transform på ydre element, stabil tekst
  const containerClasses = [
    'group relative inline-flex items-center justify-center select-none',
    'min-h-[58px] sm:min-h-[62px]',
    'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-[#007A53] focus-visible:ring-offset-2',
    'transition-colors duration-[480ms]',
    textColorClass,
    disabled ? 'opacity-60 pointer-events-none' : 'cursor-pointer',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const innerContent = (
    <>
      {/* SVG-baggrund med flydende path-morphing */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
        viewBox={`0 0 ${dimensions.w} ${dimensions.h}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          {/* Subtil skygge på knappen for dybde uden layoutshift */}
          <filter id={`arc-shadow-${filterId}`} x="-10%" y="-10%" width="125%" height="135%">
            <feDropShadow dx="0" dy="4" stdDeviation="6" floodOpacity={variant === 'white' ? '0.12' : '0.18'} floodColor={variant === 'white' ? '#000000' : '#002B1D'} />
          </filter>
        </defs>

        <path
          d={pathD}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          filter={`url(#arc-shadow-${filterId})`}
          style={{
            transition: 'fill 480ms cubic-bezier(0.22, 1, 0.36, 1), stroke 480ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
      </svg>

      {/* Indhold: Stabil tekst og vandret Rising Flow pil */}
      <span className="relative z-10 flex items-center justify-center pl-6 sm:pl-8 pr-10 sm:pr-12 py-3.5 sm:py-4">
        {icon && <span className={`mr-3 shrink-0 flex items-center ${textColorClass}`}>{icon}</span>}

        <span
          className={`font-['Montserrat',sans-serif] font-bold text-[14px] sm:text-[15px] leading-tight tracking-wide ${textColorClass}`}
        >
          {children}
        </span>

        {arrow && (
          <RisingFlowArrow
            className={`w-8 sm:w-9 h-4 sm:h-4.5 ml-3 sm:ml-4 ${arrowColorClass}`}
            style={{
              transform: activeHover ? 'translateX(4px)' : 'translateX(0px)',
              transition: 'transform 480ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          />
        )}
      </span>
    </>
  )

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)
  const handleFocus = () => setIsFocused(true)
  const handleBlur = () => setIsFocused(false)

  if (to) {
    return (
      <Link
        ref={containerRef}
        to={to}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={containerClasses}
        aria-label={ariaLabel}
        {...props}
      >
        {innerContent}
      </Link>
    )
  }

  if (href) {
    return (
      <a
        ref={containerRef}
        href={href}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={containerClasses}
        aria-label={ariaLabel}
        {...props}
      >
        {innerContent}
      </a>
    )
  }

  return (
    <button
      ref={containerRef}
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={containerClasses}
      aria-label={ariaLabel}
      {...props}
    >
      {innerContent}
    </button>
  )
}

export default ArcOrbitButton
